/**
 * Import Progress Modal Component
 *
 * Shows real-time import progress with polling for job status updates.
 * Displays statistics, errors, and completion status.
 */

import React, { useEffect, useState } from 'react';
import { Modal, Progress, Alert, Button, Collapse, Tag } from 'antd';
import { CheckCircleOutlined, CloseCircleOutlined, LoadingOutlined } from '@ant-design/icons';
import './BulkImport.css';

const ImportProgressModal = ({
  visible,
  onClose,
  jobId,
  fetchProgress
}) => {
  const [jobStatus, setJobStatus] = useState(null);
  const [polling, setPolling] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!visible || !jobId || !polling) return;

    const interval = setInterval(async () => {
      try {
        const status = await fetchProgress(jobId);
        setJobStatus(status);

        // Stop polling when job is complete or failed
        if (status.status === 'completed' || status.status === 'failed') {
          setPolling(false);
        }
      } catch (err) {
        console.error('Failed to fetch job status:', err);
        setError('Failed to fetch import status. Please try refreshing.');
        setPolling(false);
      }
    }, 2000); // Poll every 2 seconds

    return () => clearInterval(interval);
  }, [visible, jobId, polling, fetchProgress]);

  const getStatusIcon = (status) => {
    switch (status) {
      case 'completed':
        return <CheckCircleOutlined style={{ fontSize: 48, color: '#52c41a' }} />;
      case 'failed':
        return <CloseCircleOutlined style={{ fontSize: 48, color: '#ff4d4f' }} />;
      default:
        return <LoadingOutlined style={{ fontSize: 48, color: '#1890ff' }} />;
    }
  };

  const getStatusTitle = (status) => {
    switch (status) {
      case 'completed':
        return 'Import Completed Successfully';
      case 'failed':
        return 'Import Failed';
      case 'processing':
        return 'Processing Import...';
      default:
        return 'Preparing Import...';
    }
  };

  const progress = jobStatus?.progress || 0;
  const status = jobStatus?.status || 'pending';

  return (
    <Modal
      title="Import Progress"
      open={visible}
      onCancel={onClose}
      footer={[
        <Button
          key="close"
          type="primary"
          onClick={onClose}
          disabled={polling}
        >
          {polling ? 'Processing...' : 'Close'}
        </Button>
      ]}
      closable={!polling}
      maskClosable={false}
      width={700}
      className="import-progress-modal"
    >
      <div style={{ textAlign: 'center', marginBottom: 24 }}>
        {getStatusIcon(status)}
        <h3 style={{ marginTop: 16, marginBottom: 0 }}>
          {getStatusTitle(status)}
        </h3>
      </div>

      {error && (
        <Alert
          message="Error"
          description={error}
          type="error"
          showIcon
          closable
          onClose={() => setError(null)}
          style={{ marginBottom: 16 }}
        />
      )}

      <Progress
        percent={Math.round(progress)}
        status={
          status === 'completed' ? 'success' :
          status === 'failed' ? 'exception' : 'active'
        }
        strokeColor={{
          '0%': '#108ee9',
          '100%': '#87d068',
        }}
      />

      {jobStatus && (
        <div style={{ marginTop: 24 }}>
          <Alert
            message="Import Statistics"
            description={
              <div>
                <p style={{ marginBottom: 8 }}>
                  <strong>Total Rows:</strong> {jobStatus.total_rows || 0}
                </p>
                <p style={{ marginBottom: 8 }}>
                  <strong>Processed:</strong> {jobStatus.processed_rows || 0}
                </p>
                <div style={{ marginTop: 12 }}>
                  <Tag color="success" style={{ fontSize: 14, padding: '4px 12px' }}>
                    Success: {jobStatus.successful_rows || 0}
                  </Tag>
                  <Tag color="error" style={{ fontSize: 14, padding: '4px 12px' }}>
                    Failed: {jobStatus.failed_rows || 0}
                  </Tag>
                </div>
              </div>
            }
            type="info"
            showIcon
          />

          {jobStatus.errors && jobStatus.errors.length > 0 && (
            <Collapse
              style={{ marginTop: 16 }}
              items={[{
                key: 'errors',
                label: `Errors (${jobStatus.errors.length})`,
                children: (
                  <div style={{ maxHeight: 250, overflowY: 'auto' }}>
                    {jobStatus.errors.map((error, idx) => (
                      <div key={idx} className="import-error-item">
                        <div style={{ fontWeight: 600, marginBottom: 4 }}>
                          Row {error.row}
                          {error.field && (
                            <span style={{ color: '#8c8c8c', fontWeight: 'normal', marginLeft: 8 }}>
                              Field: {error.field}
                            </span>
                          )}
                        </div>
                        <div style={{ color: '#595959' }}>{error.message}</div>
                        {error.value && (
                          <div style={{ color: '#8c8c8c', fontSize: 12, marginTop: 4 }}>
                            Value: <code>{JSON.stringify(error.value)}</code>
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                )
              }]}
            />
          )}

          {status === 'completed' && jobStatus.successful_rows > 0 && (
            <Alert
              message="Success"
              description={`Successfully imported ${jobStatus.successful_rows} record(s).`}
              type="success"
              showIcon
              style={{ marginTop: 16 }}
            />
          )}

          {status === 'failed' && (
            <Alert
              message="Import Failed"
              description="The import process encountered critical errors and was stopped. Please review the errors above and try again."
              type="error"
              showIcon
              style={{ marginTop: 16 }}
            />
          )}
        </div>
      )}
    </Modal>
  );
};

export default ImportProgressModal;
